import { AccommodationArticleDetails, ArticleType } from '@nemuru-eng/js-api-client';

import { AccommodationFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Accommodation/types/AccommodationFormItem';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { numberOfNights } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/utils/numberOfNights';

type AccommodationAddress = Pick<AccommodationArticleDetails, 'address'>;

export class AccommodationItem extends Item<AccommodationFormItem> {
  declare additionalData: AccommodationAddress | undefined;

  constructor(data: AccommodationFormItem, additionalData?: AccommodationAddress) {
    super(
      {
        ...data,
        description: AccommodationItem.description(data),
      },
      additionalData,
    );
  }

  protected isCompleted(): boolean {
    return !!this.data.establishmentName && !!this.data.checkinDate && !!this.data.checkoutDate;
  }

  protected toForm(): AccommodationFormItem {
    const { establishmentName, checkinDate, checkoutDate } = this.data;

    return {
      ...this.toMinimalFormItem(),
      establishmentName,
      checkinDate,
      checkoutDate,
    } as AccommodationFormItem;
  }

  protected toDto(): object {
    return {
      name: AccommodationItem.description(this.data),
      description: this.data.description ?? AccommodationItem.description(this.data),
      checkinDate: this.data.checkinDate,
      checkoutDate: this.data.checkoutDate,
      establishmentName: this.data.establishmentName?.trim() || '',
      address: this.additionalData?.address || {
        streetAddress: 'unknown',
        postalCode: '08001',
        city: 'unknown',
        country: 'ESP',
      },
    };
  }

  public static fromOrderLineDto(units: number, article: AccommodationArticleDetails): AccommodationItem {
    const formData: AccommodationFormItem = {
      // Minimal form item fields
      units: units.toString(),
      vat: '21', // FIXME: Default VAT
      price: article.price.amount.toFixed(2).toString(),
      category: article.category,

      // ArticleType-specific fields
      type: ArticleType.ACCOMMODATION,
      establishmentName: article.establishment || article.name || '',
      checkinDate: article.checkin as Date,
      checkoutDate: article.checkout as Date,
    };

    const additionalData: AccommodationAddress = {
      address: article.address,
    };

    return new AccommodationItem(formData, additionalData);
  }

  private static description(data: AccommodationFormItem): string {
    if (!data.establishmentName || !data.checkinDate || !data.checkoutDate) return data.description ?? '';
    return `${data.establishmentName?.trim()} (${numberOfNights(data.checkinDate, data.checkoutDate)} noches)`;
  }
}
